import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from '../hooks/use-toast';
import { challenges } from '../mockData';
import { CreditCard, DollarSign, CheckCircle } from 'lucide-react';

const Dashboard = () => {
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [paymentStep, setPaymentStep] = useState('select');
  const [formData, setFormData] = useState({
    email: '',
    cardNumber: '',
    expiry: '',
    cvv: '',
    name: ''
  });

  const handleChallengeSelect = (challenge) => {
    setSelectedChallenge(challenge);
    setPaymentStep('payment');
  };

  const handlePayment = (e) => {
    e.preventDefault();
    // Mock payment processing
    setTimeout(() => {
      setPaymentStep('success');
      toast({
        title: 'Payment Successful!',
        description: 'Your account has been created. Check your email for login details.',
      });
    }, 1500);
  };

  return (
    <div className="min-h-screen pt-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {paymentStep === 'select' && (
          <div>
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Choose Your Challenge
              </h1>
              <p className="text-xl text-gray-600">
                Select an account size to get started
              </p>
            </div>

            <Tabs defaultValue="challenge" className="w-full">
              <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-12">
                <TabsTrigger value="challenge">Challenge</TabsTrigger>
                <TabsTrigger value="instant">Instant Funding</TabsTrigger>
              </TabsList>
              
              <TabsContent value="challenge">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {challenges.map((challenge) => (
                    <Card
                      key={challenge.id}
                      className={`border-2 cursor-pointer hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 ${
                        challenge.featured ? 'border-[#D4AF37] shadow-xl' : 'border-gray-200'
                      }`}
                      onClick={() => handleChallengeSelect(challenge)}
                    >
                      <CardContent className="p-6">
                        {challenge.featured && (
                          <div className="bg-[#D4AF37] text-black text-sm font-bold py-1 px-3 rounded-full inline-block mb-4">
                            MOST POPULAR
                          </div>
                        )}
                        <h3 className="text-3xl font-bold text-[#D4AF37] mb-2">
                          {challenge.size}
                        </h3>
                        <p className="text-2xl font-bold text-gray-900 mb-6">{challenge.cost}</p>
                        <div className="space-y-3 mb-6">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Profit Target:</span>
                            <span className="font-semibold">{challenge.profitTarget}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Max Drawdown:</span>
                            <span className="font-semibold">{challenge.maxDrawdown}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Profit Split:</span>
                            <span className="font-semibold text-[#D4AF37]">{challenge.profitSplit}</span>
                          </div>
                        </div>
                        <Button className="w-full bg-[#D4AF37] hover:bg-[#B8941F] text-black font-semibold">
                          Select This Challenge
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>
              
              <TabsContent value="instant">
                <div className="text-center py-12">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    Instant Funding Options
                  </h3>
                  <p className="text-gray-600 mb-8">
                    Skip the evaluation and start trading immediately
                  </p>
                  <Link to="/instant-funding">
                    <Button size="lg" className="bg-[#D4AF37] hover:bg-[#B8941F] text-black font-semibold">
                      View Instant Funding Plans
                    </Button>
                  </Link>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        )}

        {paymentStep === 'payment' && selectedChallenge && (
          <div className="max-w-2xl mx-auto">
            <Button
              variant="ghost"
              onClick={() => {
                setPaymentStep('select');
                setSelectedChallenge(null);
              }}
              className="mb-6"
            >
              ← Back to Selection
            </Button>
            
            <Card className="border-none shadow-xl">
              <CardContent className="p-8">
                <div className="flex items-center justify-between mb-8 pb-6 border-b">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Checkout</h2>
                    <p className="text-gray-600">Complete your purchase</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Account Size</p>
                    <p className="text-3xl font-bold text-[#D4AF37]">{selectedChallenge.size}</p>
                  </div>
                </div>

                <form onSubmit={handlePayment} className="space-y-6">
                  <div>
                    <Label htmlFor="email" className="text-gray-700 font-semibold">
                      Email Address
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="your.email@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="mt-2"
                    />
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg">
                    <div className="flex items-center mb-4">
                      <CreditCard className="text-[#D4AF37] mr-2" />
                      <h3 className="text-lg font-bold text-gray-900">Payment Details</h3>
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="cardName" className="text-gray-700">
                          Cardholder Name
                        </Label>
                        <Input
                          id="cardName"
                          placeholder="John Doe"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          required
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label htmlFor="cardNumber" className="text-gray-700">
                          Card Number
                        </Label>
                        <Input
                          id="cardNumber"
                          placeholder="1234 5678 9012 3456"
                          value={formData.cardNumber}
                          onChange={(e) => setFormData({ ...formData, cardNumber: e.target.value })}
                          required
                          maxLength={19}
                          className="mt-2"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="expiry" className="text-gray-700">
                            Expiry Date
                          </Label>
                          <Input
                            id="expiry"
                            placeholder="MM/YY"
                            value={formData.expiry}
                            onChange={(e) => setFormData({ ...formData, expiry: e.target.value })}
                            required
                            maxLength={5}
                            className="mt-2"
                          />
                        </div>
                        <div>
                          <Label htmlFor="cvv" className="text-gray-700">
                            CVV
                          </Label>
                          <Input
                            id="cvv"
                            placeholder="123"
                            value={formData.cvv}
                            onChange={(e) => setFormData({ ...formData, cvv: e.target.value })}
                            required
                            maxLength={4}
                            className="mt-2"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-[#1A1A1A] p-6 rounded-lg">
                    <div className="flex justify-between mb-2">
                      <span className="text-gray-300">Challenge Fee:</span>
                      <span className="font-semibold text-white">{selectedChallenge.cost}</span>
                    </div>
                    <div className="flex justify-between mb-2">
                      <span className="text-gray-300">Processing Fee:</span>
                      <span className="font-semibold text-white">$0.00</span>
                    </div>
                    <div className="border-t border-[#D4AF37] pt-2 mt-2 flex justify-between">
                      <span className="text-xl font-bold text-white">Total:</span>
                      <span className="text-xl font-bold text-[#D4AF37]">{selectedChallenge.cost}</span>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    className="w-full bg-[#D4AF37] hover:bg-[#B8941F] text-black font-semibold text-lg py-6"
                  >
                    <DollarSign className="mr-2" />
                    Complete Purchase
                  </Button>
                </form>

                <p className="text-sm text-gray-500 text-center mt-6">
                  Your payment is secure and encrypted. By completing this purchase, you agree to our Terms & Conditions.
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {paymentStep === 'success' && selectedChallenge && (
          <div className="max-w-2xl mx-auto text-center">
            <Card className="border-none shadow-xl">
              <CardContent className="p-12">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
                  <CheckCircle className="text-green-600" size={48} />
                </div>
                <h2 className="text-4xl font-bold text-gray-900 mb-4">
                  Welcome to FundedLab!
                </h2>
                <p className="text-xl text-gray-600 mb-8">
                  Your {selectedChallenge.size} challenge account has been created successfully.
                </p>
                <div className="bg-gray-50 p-6 rounded-lg mb-8">
                  <h3 className="font-bold text-gray-900 mb-4">Next Steps:</h3>
                  <ol className="text-left space-y-3 text-gray-700">
                    <li className="flex items-start">
                      <span className="font-bold text-[#D4AF37] mr-2">1.</span>
                      <span>Check your email for login credentials and platform access</span>
                    </li>
                    <li className="flex items-start">
                      <span className="font-bold text-[#D4AF37] mr-2">2.</span>
                      <span>Download your trading platform (MT4/MT5)</span>
                    </li>
                    <li className="flex items-start">
                      <span className="font-bold text-[#D4AF37] mr-2">3.</span>
                      <span>Start trading and reach your profit target!</span>
                    </li>
                  </ol>
                </div>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button size="lg" className="bg-[#D4AF37] hover:bg-[#B8941F] text-black font-semibold">
                    Access Dashboard
                  </Button>
                  <Link to="/">
                    <Button size="lg" variant="outline" className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black">
                      Return Home
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;